import { MailIcon, OfficeBuildingIcon } from '@heroicons/react/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CardTitle } from 'shared-ui/lib/components/CardTitle';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { GenericTable } from 'shared-ui/lib/customs/react/generic-table/GenericTable';
import { partnershipColumns } from '../../columns/partnership.columns';
import { ConfirmModal } from '../../components/ConfirmModal';
import { PartnerGroupsModal } from '../../components/PartnerGroupsModal';
import {
  MyActivePartnershipsQuery,
  MyIncomingPartnerInvitesQuery,
  useEndPartnerCustomerByBusinessMutation,
  useMyActivePartnershipsQuery,
  useMyIncomingPartnerInvitesQuery,
  usePartnershipGroupsQuery,
  useRespondToPartnerInviteMutation,
  useUpdatePartnerCustomerGroupsMutation,
} from '../../generated/graphql';

type PartnershipTab = 'invites' | 'partnerships';
type InviteRow = MyIncomingPartnerInvitesQuery['myIncomingPartnerInvites'][0];
type PartnershipRow = MyActivePartnershipsQuery['myActivePartnerships'][0];

const tabClassName = (active: boolean): string =>
  `appearance-none flex-1 inline-flex items-center justify-center px-6 py-3 text-base font-semibold border-0 border-b-4 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
    active
      ? 'bg-white text-primary border-primary'
      : 'bg-gray-100 text-gray-color border-transparent hover:bg-gray-200 hover:text-gray-900'
  }`;

const tableClassNames =
  'bg-white min-w-full divide-y divide-gray-200 overflow-hidden border-b border-gray-200 rounded-lg';

export const PartnershipScene = (): JSX.Element => {
  const { t } = useTranslation('PartnershipScene');
  const [tab, setTab] = useState<PartnershipTab>('partnerships');
  const [hasInitializedTab, setHasInitializedTab] = useState(false);
  const [inviteToAccept, setInviteToAccept] = useState<InviteRow | null>(null);
  const [inviteToReject, setInviteToReject] = useState<InviteRow | null>(null);
  const [partnershipToEnd, setPartnershipToEnd] = useState<PartnershipRow | null>(null);
  const [partnershipToEdit, setPartnershipToEdit] = useState<PartnershipRow | null>(null);

  const invitesQuery = useMyIncomingPartnerInvitesQuery(setupQueryConfig());
  const partnershipsQuery = useMyActivePartnershipsQuery(setupQueryConfig());
  const groupsQuery = usePartnershipGroupsQuery(setupQueryConfig());
  const [respondToInvite] = useRespondToPartnerInviteMutation();
  const [updateGroups] = useUpdatePartnerCustomerGroupsMutation();
  const [endPartnership] = useEndPartnerCustomerByBusinessMutation();

  const inviteCount = invitesQuery.data?.myIncomingPartnerInvites.length ?? 0;
  const partnershipCount = partnershipsQuery.data?.myActivePartnerships.length ?? 0;
  const groups = groupsQuery.data?.groups.list ?? [];

  useEffect(() => {
    if (hasInitializedTab || !invitesQuery.data) return;
    setHasInitializedTab(true);
    if (invitesQuery.data.myIncomingPartnerInvites.length > 0) {
      setTab('invites');
    }
  }, [hasInitializedTab, invitesQuery.data]);

  const refetchLists = async (): Promise<void> => {
    await Promise.all([invitesQuery.refetch(), partnershipsQuery.refetch()]);
  };

  const handleConfirmAccept = async (groupIds: string[]): Promise<void> => {
    if (!inviteToAccept) return;
    const result = await respondToInvite({
      variables: { id: inviteToAccept.id, accept: true, groupIds },
    });
    setInviteToAccept(null);
    if (!result.data) return;
    toast.success(t('acceptSuccess'));
    await refetchLists();
  };

  const handleConfirmReject = async (): Promise<void> => {
    if (!inviteToReject) return;
    const result = await respondToInvite({
      variables: { id: inviteToReject.id, accept: false },
    });
    setInviteToReject(null);
    if (!result.data) return;
    toast.success(t('rejectSuccess'));
    await refetchLists();
  };

  const handleConfirmEditGroups = async (groupIds: string[]): Promise<void> => {
    if (!partnershipToEdit) return;
    const result = await updateGroups({
      variables: { id: partnershipToEdit.id, groupIds },
    });
    setPartnershipToEdit(null);
    if (!result.data) return;
    toast.success(t('editGroupsSuccess'));
    await refetchLists();
  };

  const handleConfirmEnd = async (): Promise<void> => {
    if (!partnershipToEnd) return;
    const result = await endPartnership({ variables: { id: partnershipToEnd.id } });
    setPartnershipToEnd(null);
    if (!result.data) return;
    toast.success(t('endSuccess'));
    await refetchLists();
  };

  const inviteColumns = [
    partnershipColumns.partnerName,
    partnershipColumns.invitedAt,
    partnershipColumns.inviteActions(setInviteToAccept, setInviteToReject),
  ];

  const activeColumns = [
    partnershipColumns.partnerName,
    partnershipColumns.startedAt,
    partnershipColumns.groupsSummary(groups),
    partnershipColumns.partnershipActions(setPartnershipToEdit, setPartnershipToEnd),
  ];

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="flex border-b border-gray-200" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'invites'}
          className={tabClassName(tab === 'invites')}
          onClick={() => setTab('invites')}
        >
          <MailIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          <span>{t('tabInvites')}</span>
          {inviteCount > 0 ? (
            <span
              className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-white text-xs font-bold leading-none flex-shrink-0"
              aria-label={`${inviteCount} ${t('tabInvites')}`}
            >
              {inviteCount}
            </span>
          ) : undefined}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'partnerships'}
          className={tabClassName(tab === 'partnerships')}
          onClick={() => setTab('partnerships')}
        >
          <OfficeBuildingIcon className="h-5 w-5 mr-2 flex-shrink-0" aria-hidden="true" />
          {t('tabPartnerships')}
        </button>
      </div>
      <div className="p-4">
        <CardTitle>{tab === 'invites' ? t('tabInvites') : t('tabPartnerships')}</CardTitle>
        {tab === 'invites' ? (
          !invitesQuery.loading && inviteCount === 0 ? (
            <p className="text-sm text-gray-color">{t('emptyInvites')}</p>
          ) : (
            <div className="overflow-x-auto">
              <GenericTable
                columns={inviteColumns}
                data={invitesQuery.data?.myIncomingPartnerInvites}
                tableClassNames={tableClassNames}
                tHeadClassNames="bg-gray-50"
                thClassNames="px-6 py-3 text-left text-xs font-medium text-gray-color uppercase tracking-wider"
                tdClassNames="px-6 py-4 text-sm font-medium"
                trClassNames="even:bg-gray-50"
              />
            </div>
          )
        ) : !partnershipsQuery.loading && partnershipCount === 0 ? (
          <p className="text-sm text-gray-color">{t('emptyPartnerships')}</p>
        ) : (
          <div className="overflow-x-auto">
            <GenericTable
              columns={activeColumns}
              data={partnershipsQuery.data?.myActivePartnerships}
              tableClassNames={tableClassNames}
              tHeadClassNames="bg-gray-50"
              thClassNames="px-6 py-3 text-left text-xs font-medium text-gray-color uppercase tracking-wider"
              tdClassNames="px-6 py-4 text-sm font-medium"
              trClassNames="even:bg-gray-50"
            />
          </div>
        )}
      </div>
      <PartnerGroupsModal
        isOpen={Boolean(inviteToAccept)}
        onCancel={() => setInviteToAccept(null)}
        onConfirm={handleConfirmAccept}
        title={t('acceptConfirmTitle')}
        description={t('acceptGroupsDescription')}
        groups={groups}
        initialSelectedIds={[]}
        confirmLabel={t('acceptConfirm')}
        cancelLabel={t('confirmCancel')}
      />
      <ConfirmModal
        isOpen={Boolean(inviteToReject)}
        onCancel={() => setInviteToReject(null)}
        onConfirm={handleConfirmReject}
        title={t('rejectConfirmTitle')}
        description={t('rejectConfirmDescription')}
        confirmLabel={t('rejectConfirm')}
        cancelLabel={t('confirmCancel')}
        variant="danger"
      />
      <PartnerGroupsModal
        isOpen={Boolean(partnershipToEdit)}
        onCancel={() => setPartnershipToEdit(null)}
        onConfirm={handleConfirmEditGroups}
        title={t('editGroupsTitle')}
        description={t('editGroupsDescription')}
        groups={groups}
        initialSelectedIds={partnershipToEdit?.allowedGroupIds ?? []}
        confirmLabel={t('editGroupsSave')}
        cancelLabel={t('confirmCancel')}
      />
      <ConfirmModal
        isOpen={Boolean(partnershipToEnd)}
        onCancel={() => setPartnershipToEnd(null)}
        onConfirm={handleConfirmEnd}
        title={t('endConfirmTitle')}
        description={t('endConfirmDescription')}
        confirmLabel={t('endConfirm')}
        cancelLabel={t('confirmCancel')}
        variant="danger"
      />
    </div>
  );
};
